import React, { useState } from 'react';
import { X, Upload, AlertTriangle } from 'lucide-react';
import { useWorkOrderParser } from '../hooks/useWorkOrderParser';

interface ImportWorkOrderModalProps {
  onClose: () => void;
  onImport: (data: any) => Promise<void>;
}

export const ImportWorkOrderModal: React.FC<ImportWorkOrderModalProps> = ({
  onClose,
  onImport,
}) => {
  const [text, setText] = useState('');
  const { parseWorkOrder, loading, error } = useWorkOrderParser();
  const [parsedData, setParsedData] = useState<any>(null);

  const handleParse = async () => {
    const data = await parseWorkOrder(text);
    if (data) {
      setParsedData(data);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg max-w-3xl w-full">
        <div className="p-6">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-semibold text-gray-900">Import Work Order</h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {error && (
            <div className="mb-4 p-4 bg-red-50 text-red-800 rounded-lg flex items-start">
              <AlertTriangle className="w-5 h-5 mr-2 flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-medium">Error parsing work order</p>
                <p className="mt-1">{error}</p>
              </div>
            </div>
          )}

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Paste Work Order Text
              </label>
              <textarea
                value={text}
                onChange={(e) => setText(e.target.value)}
                rows={10}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Paste the work order text here..."
              />
            </div>

            {parsedData && (
              <div className="bg-gray-50 p-4 rounded-lg">
                <h3 className="text-lg font-medium text-gray-900 mb-4">Parsed Information</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-gray-500">Customer</p>
                    <p className="font-medium">{parsedData.firstName} {parsedData.lastName}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Phone</p>
                    <p className="font-medium">{parsedData.phone}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Email</p>
                    <p className="font-medium">{parsedData.email || 'N/A'}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Address</p>
                    <p className="font-medium">
                      {parsedData.address}, {parsedData.city}, {parsedData.state} {parsedData.zipCode}
                    </p>
                  </div>
                  {parsedData.modelNumber && (
                    <div>
                      <p className="text-sm text-gray-500">Model Number</p>
                      <p className="font-medium">{parsedData.modelNumber}</p>
                    </div>
                  )}
                  {parsedData.serialNumber && (
                    <div>
                      <p className="text-sm text-gray-500">Serial Number</p>
                      <p className="font-medium">{parsedData.serialNumber}</p>
                    </div>
                  )}
                  {parsedData.svo && (
                    <div>
                      <p className="text-sm text-gray-500">SVO</p>
                      <p className="font-medium">{parsedData.svo}</p>
                    </div>
                  )}
                  {parsedData.brand && (
                    <div>
                      <p className="text-sm text-gray-500">Brand</p>
                      <p className="font-medium">{parsedData.brand}</p>
                    </div>
                  )}
                </div>
              </div>
            )}

            <div className="flex justify-end space-x-3">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg"
              >
                Cancel
              </button>
              {!parsedData ? (
                <button
                  onClick={handleParse}
                  disabled={!text.trim() || loading}
                  className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                >
                  <Upload className="w-4 h-4 mr-2" />
                  {loading ? 'Parsing...' : 'Parse Text'}
                </button>
              ) : (
                <button
                  onClick={() => onImport(parsedData)}
                  className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
                >
                  Create Work Order
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}